import Vue from "vue"
import Router from "vue-router"
import _ from "lodash"
import axios from "axios"
import Logger from "@/plugins/logger"
import { store, session } from "@/api/store"
import { getUserData, SetHeader, getNetworkType } from "@/api/access"
import { DevicesUpdateAPI } from "@/api"
import { MainFrame, LangFrame } from "@/components/frame"
import { alreadyAuth, commonGuard } from "./func"
import { resetSessionExpiredFlag } from "@/api/config"
import { getCompanyFunction } from "@/api/util"
import moment from "moment"

import {
  LoginComponent,
  JoinComponent,
  WelcomeComponent,
  JoinDoneComponent,
  ForgotComponent,
  ResetPwComponent,
} from "@/components/access"

import { TicketComponent, TicketRequestComponent } from "@/components/ticket"
import { ErrorComponent } from "@/components/error"
import { setI18nLanguage, getCurrentLanguage } from "@/i18n"

Vue.use(Router)

const files = require.context("./modules", true, /\.js$/)
const modules = files.keys().reduce((modules, modulePath) => {
  modules = _.concat(modules, files(modulePath).default)
  return modules
}, [])

const pageRouter = [
  {
    path: "",
    name: "login",
    beforeEnter: alreadyAuth,
    component: LoginComponent,
  },
  {
    path: "join-us",
    name: "join-us",
    component: JoinComponent,
  },
  {
    path: "forgot",
    name: "forgot",
    component: ForgotComponent,
  },
  {
    path: "forgot/:type",
    name: "forgot-type",
    component: ForgotComponent,
  },
  {
    path: "access/auth",
    name: "access-auth",
    component: WelcomeComponent,
  },
  {
    path: "access/retypepassword",
    name: "access-retypepassword",
    component: ResetPwComponent,
  },
  {
    path: "join-done",
    name: "join-done",
    component: JoinDoneComponent,
  },
  {
    path: "welcome",
    name: "welcome",
    component: WelcomeComponent,
  },
  {
    path: "access-ticket",
    redirect: { name: "ticket-request" },
    component: MainFrame,
    children: [
      {
        path: "",
        redirect: { name: "ticket-request" },
        component: TicketComponent,
        children: [
          {
            path: "request",
            name: "ticket-request",
            component: TicketRequestComponent,
            beforeEnter: commonGuard,
          },
        ],
      },
    ],
  },
  {
    path: "error/:code",
    name: "error",
    component: ErrorComponent,
    props: true,
  },
  { path: "*", component: ErrorComponent, props: { code: 404 } },
]

const router = new Router({
  routes: [
    {
      path: "/",
      redirect: () => ({ name: "login", params: { lang: getCurrentLanguage() } }),
    },
    {
      path: "/access/auth_token",
      beforeEnter(to, from, next) {
        let { token, redirect } = to.query
        if (!token) return next({ name: "login", params: { lang: "ko" } })
        const skipHistoryFlag = true
        getUserData(
          token,
          (err, userData) => {
            if (err) return next("error")
            if (!redirect) redirect = { name: "license", params: { lang: "ko" } }

            // 관리자 로그인인 경우 로그인할 때 처리해야 할 정보를 받을 수 없기 때문에 강제로 넣음.
            store.set("networkType", 'internal')
            store.set('auth', {account_type: userData.license.list[0].account_type})

            next(redirect)
          },
          skipHistoryFlag,
        )
      },
      component: {
        template: "<div>- PROCESSING...</div>",
      },
    },
    {
      path: "/access/force_login",
      async beforeEnter(to, from, next) {
        const { token } = to.query
        if (!token) return next({ name: "login", params: { lang: "ko" } })

        try {
          // 1. 백엔드에 토큰 검증 요청 (60초 만료 토큰)
          const response = await axios.post(
            `${process.env.VUE_APP_API_ADDRESS}/superadmin/api/${process.env.VUE_APP_API_VERSION}/force-login/verify`,
            { force_login_token: token }
          )
          const loginData = response.data

          // 2. 세션 만료 플래그 리셋
          resetSessionExpiredFlag()

          // 3. 토큰 저장
          session.set("token", loginData.token)
          session.set("refresh_token", loginData.refresh_token)
          SetHeader("token", loginData.token)

          // 4. 인증 정보 저장 (networkType은 manager로 설정)
          const networkType = getNetworkType("manager")
          store.set("auth", loginData)
          store.set("networkType", networkType)
          store.set("isSuperadminLogin", true)

          // 5. 세션 만료 시간 설정
          const sessionTimeout = await getCompanyFunction("manager_session_timeout")
          store.set("forceExpireTime", moment().add(sessionTimeout, "minutes").toDate())

          // 6. 사용자 데이터 조회 후 리다이렉트
          const skipHistoryFlag = true
          getUserData(
            loginData.token,
            (err, userData) => {
              if (err) {
                Logger.error("[FORCE LOGIN] getUserData 실패:", err)
                return next({ name: "login", params: { lang: "ko" } })
              }
              next({ name: "license", params: { lang: "ko" } })
            },
            skipHistoryFlag
          )
        } catch (err) {
          Logger.error("[FORCE LOGIN] 토큰 검증 실패:", err)
          // 토큰 만료 또는 검증 실패 시 로그인 페이지로
          next({ name: "login", params: { lang: "ko" } })
        }
      },
      component: {
        template: "<div>- 강제 로그인 처리중...</div>",
      },
    },
    {
      path: "/:lang",
      component: LangFrame,
      children: _.concat(modules, pageRouter),
    },
  ],
})

let deviceCheckApiTimer = null
router.beforeEach((to, from, next) => {
  if (to.path === "/access/auth_token" || to.path === "/access/force_login") {
    return next()
  }
  if (deviceCheckApiTimer === null && store.get("manager_device_id") && process.env.VUE_APP_CHECKTOOL_DEVICE_UPDATE === 1) {
    const stored = store.get("user")
    if (stored && stored.token) {
      deviceCheckApiTimer = setInterval(() => {
        if (store.get("manager_device_id")) {
          DevicesUpdateAPI.put("", {
            input_type: "last_alive_check_time",
            device_id: store.get("manager_device_id"),
            last_alive_check_time: moment().format("YYYY-MM-DD HH:mm:ss"),
          })
        }
      }, 5000)
    }
  }
  const { lang } = to.params
  if (!lang || !/ko|en|jp/.test(lang)) {
    next("/ko/")
  } else {
    let current = getCurrentLanguage()
    if (current !== lang) {
      Logger.info(`LANG CHANGED: ${lang}`)
      store.set("lang", lang)
      setI18nLanguage(lang)
      next()
      setTimeout(() => window.location.reload(), 300)
      // loadLanguageAsync(lang).then(() => next())
    } else {
      next()
    }
  }
})

export default router
